// ============================================
// User & Authentication Types
// ============================================

export interface User {
  id: number;
  email: string;
  name: string;
  avatar_url?: string;
  organization_id?: number;
  role: 'owner' | 'admin' | 'member';
  subscription_tier: SubscriptionTier;
  credits_remaining: number;
  credits_expires_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo_url?: string;
  owner_id: number;
  subscription_tier: SubscriptionTier;
  credits_used: number;
  credits_limit: number;
  created_at: string;
}

export interface TeamMember {
  id: number;
  user_id: number;
  organization_id: number;
  role: 'admin' | 'member';
  invited_by: number;
  joined_at: string;
}

// ============================================
// AI Employee Types
// ============================================

export type EmployeeId =
  | 'agent'   // Conversational Agent (Universal Expert & Router)
  | 'aria'    // Social Media Manager
  | 'nova'    // Customer Support
  | 'atlas'   // Data Analyst
  | 'sage'    // Business Strategist
  | 'echo'    // Email Marketing
  | 'pulse'   // Personal Growth
  | 'scout'   // Recruiter
  | 'ink'     // Copywriter
  | 'flux'    // eCommerce Manager
  | 'spark'   // Sales Specialist
  | 'orbit'   // SEO Expert
  | 'sync'    // Virtual Assistant
  | 'code'    // Developer Assistant
  | 'pixel'   // Design Assistant
  | 'legal'   // Compliance Assistant
  | 'mvp'     // MVP Maker / Prototype Builder
  | 'slides'; // Presentation Maker

export interface AIEmployee {
  id: EmployeeId;
  name: string;
  role: string;
  description: string;
  avatar: string;
  color: string;
  gradient: string;
  capabilities: string[];
  useCases: UseCase[];
  category: EmployeeCategory;
  isNew?: boolean;
  isPremium?: boolean;
  isComingSoon?: boolean;
}

export type EmployeeCategory =
  | 'general'
  | 'marketing'
  | 'sales'
  | 'support'
  | 'operations'
  | 'creative'
  | 'technical'
  | 'personal';

export interface UseCase {
  id: string;
  title: string;
  description: string;
  prompt: string;
  inputs?: UseCaseInput[];
  category: string;
}

export interface UseCaseInput {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'url' | 'file';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

// ============================================
// Conversation & Messages
// ============================================

export interface Conversation {
  id: number;
  user_id: number;
  employee_id: EmployeeId;
  title: string;
  last_message_at: string;
  message_count: number;
  created_at: string;
}

export interface Message {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  tokens_used?: number;
  attachments?: Attachment[];
  created_at: string;
}

export interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

// ============================================
// Brain AI (Knowledge Base)
// ============================================

export interface BrainDocument {
  id: number;
  organization_id: number;
  name: string;
  type: 'pdf' | 'doc' | 'txt' | 'csv' | 'url';
  source_url?: string;
  content_hash: string;
  chunks_count: number;
  status: 'processing' | 'ready' | 'error';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface BrainConfig {
  id: number;
  organization_id: number;
  custom_instructions?: string;
  tone: 'professional' | 'casual' | 'friendly' | 'formal';
  language: string;
  response_length: 'concise' | 'balanced' | 'detailed';
  created_at: string;
  updated_at: string;
}

// ============================================
// Tasks & Automation
// ============================================

export interface Task {
  id: number;
  organization_id: number;
  user_id: number;
  employee_id: EmployeeId;
  use_case_id?: string;
  title: string;
  description?: string;
  input_data: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  status: TaskStatus;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  credits_used: number;
  created_at: string;
}

export type TaskStatus =
  | 'pending'
  | 'scheduled'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface Automation {
  id: number;
  organization_id: number;
  employee_id: EmployeeId;
  name: string;
  description?: string;
  trigger_type: TriggerType;
  trigger_config: Record<string, unknown>;
  action_config: Record<string, unknown>;
  is_active: boolean;
  last_run_at?: string;
  run_count: number;
  created_at: string;
}

export type TriggerType =
  | 'schedule'    // Cron-based
  | 'webhook'     // External trigger
  | 'event'       // Internal event
  | 'manual';     // User-triggered

// ============================================
// Integrations
// ============================================

export interface Integration {
  id: number;
  organization_id: number;
  provider: IntegrationProvider;
  name: string;
  credentials_encrypted: string;
  config: Record<string, unknown>;
  status: 'connected' | 'disconnected' | 'error';
  last_sync_at?: string;
  created_at: string;
}

export type IntegrationProvider =
  | 'google'
  | 'microsoft'
  | 'slack'
  | 'discord'
  | 'notion'
  | 'trello'
  | 'asana'
  | 'hubspot'
  | 'salesforce'
  | 'shopify'
  | 'stripe'
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'webhook';

// ============================================
// Billing & Subscriptions
// ============================================

export type SubscriptionTier = 'free' | 'pro' | 'student' | 'professional' | 'enterprise';

export interface Subscription {
  id: number;
  organization_id: number;
  tier: SubscriptionTier;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: 'active' | 'past_due' | 'cancelled' | 'trialing';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
}

export interface UsageRecord {
  id: number;
  organization_id: number;
  period_start: string;
  period_end: string;
  messages_count: number;
  tasks_count: number;
  documents_count: number;
  tokens_used: number;
  credits_used: number;
}

export interface PricingPlan {
  id: SubscriptionTier;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  employees_limit: number | 'unlimited';
  messages_limit: number | 'unlimited';
  features: string[];
  highlighted?: boolean;
}

// ============================================
// API Types
// ============================================

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// ============================================
// Presentation Types
// ============================================

export type PresentationTheme =
  | 'dark'
  | 'light'
  | 'gradient'
  | 'colorful'
  | 'corporate'
  | 'creative'
  | 'minimal'
  | 'bold';

export type PresentationStyle =
  | 'professional'
  | 'creative'
  | 'minimal'
  | 'bold'
  | 'corporate';

export type SlideAnimation =
  | 'none'
  | 'fade-in'
  | 'slide-up'
  | 'slide-left'
  | 'slide-right'
  | 'zoom-in'
  | 'bounce'
  | 'flip';

export type SlideTransition =
  | 'none'
  | 'fade'
  | 'slide'
  | 'zoom'
  | 'flip'
  | 'cube'
  | 'dissolve';

export type TextAnimation =
  | 'none'
  | 'typewriter'
  | 'fade-in-up'
  | 'fade-in-down'
  | 'bounce-in'
  | 'zoom-in';

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  preview?: string;
  colors: {
    background: string;
    primary: string;
    secondary: string;
    text: string;
    accent: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
  category: 'minimal' | 'bold' | 'corporate' | 'creative' | 'dark' | 'light' | 'professional';
}

export interface TypographySettings {
  headingFont: string;
  bodyFont: string;
  headingSize: 'small' | 'medium' | 'large' | 'xlarge';
  bodySize: 'small' | 'medium' | 'large';
  headingWeight: 'normal' | 'medium' | 'bold' | 'extrabold';
  lineHeight: 'tight' | 'normal' | 'relaxed';
}

export interface AnimationSettings {
  slideTransition: SlideTransition;
  slideTransitionDuration: number;
  elementAnimation: SlideAnimation;
  textAnimation: TextAnimation;
  autoAdvance: boolean;
  autoAdvanceDelay: number;
}

export interface PresentationSlide {
  id: string;
  number: number;
  type: 'title' | 'content' | 'section' | 'image' | 'quote' | 'comparison' | 'timeline';
  title: string;
  subtitle?: string;
  content: string[];
  notes?: string;
  visual?: string;
  image?: string; // URL to image (Unsplash or other source)
  imageAlt?: string; // Alt text for accessibility
  animation?: SlideAnimation;
  transition?: SlideTransition;
  layout?: 'default' | 'title' | 'title-subtitle' | 'title-bullets' | 'two-column' | 'three-column' | 'comparison' | 'image-left' | 'image-right' | 'full-image' | 'quote' | 'timeline' | 'stats' | 'feature-grid-2x2' | 'feature-grid-3x2' | 'icon-grid' | 'agenda' | 'thank-you' | 'section-divider' | 'team' | 'process-steps' | 'centered';
  backgroundColor?: string;
}

export interface PresentationData {
  id: string;
  title: string;
  subtitle?: string;
  author?: string;
  theme: PresentationTheme;
  style: PresentationStyle;
  slides: PresentationSlide[];
  metadata?: {
    totalSlides: number;
    createdAt: string;
    updatedAt?: string;
    topic: string;
  };
  customization?: {
    themePreset?: string;
    typography: TypographySettings;
    animations: AnimationSettings;
    customColors?: Record<string, string>;
  };
}

export interface Presentation {
  id: string;
  user_id: number;
  conversation_id?: number;
  title: string;
  data: PresentationData;
  credits_used: number;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
}

export const PRESENTATION_CREDITS = {
  GENERATION: 5,
  THEME_CHANGE: 3,
  TYPOGRAPHY_CHANGE: 2,
  ANIMATION_CHANGE: 2,
  REGENERATE_SLIDE: 2,
  AI_THEME_GENERATION: 3,
} as const;

// ============================================
// Connector Types (Employee Integration Access)
// ============================================

export type ConnectorProvider = 'gmail' | 'google_docs' | 'google_calendar' | 'google_drive' | 'trello';

// Gmail Actions
export type GmailAction = 'read_emails' | 'send_email' | 'create_draft' | 'search_emails' | 'get_labels';

// Google Docs Actions
export type GoogleDocsAction = 'create_doc' | 'read_doc' | 'update_doc' | 'list_docs';

// Google Calendar Actions
export type GoogleCalendarAction = 'create_event' | 'read_events' | 'update_event' | 'delete_event' | 'list_calendars';

// Google Drive Actions
export type GoogleDriveAction = 'list_files' | 'search_files' | 'download_file' | 'get_file_info';

// Trello Actions
export type TrelloAction = 'create_card' | 'update_card' | 'move_card' | 'add_comment' | 'list_boards' | 'list_cards' | 'list_lists' | 'create_list';

// Slack Actions
export type SlackAction = 'list_channels' | 'get_channel_messages' | 'send_message' | 'reply_to_thread' | 'list_users' | 'search_messages' | 'schedule_message';

// Discord Actions
export type DiscordAction =
  | 'list_guilds'           // List servers the bot is in
  | 'list_channels'         // List channels in a guild
  | 'get_channel_messages'  // Get messages from a channel
  | 'send_message'          // Send message to a channel
  | 'reply_to_message'      // Reply to a specific message
  | 'list_members'          // List guild members
  | 'get_user'              // Get user profile
  | 'send_dm';              // Send direct message

// Union of all connector actions
export type ConnectorAction = GmailAction | GoogleDocsAction | GoogleCalendarAction | GoogleDriveAction | TrelloAction | SlackAction | DiscordAction;

// Read vs Write action classification
export const READ_ACTIONS: ConnectorAction[] = [
  'read_emails', 'search_emails', 'get_labels',
  'read_doc', 'list_docs',
  'read_events', 'list_calendars',
  'list_files', 'search_files', 'download_file', 'get_file_info',
  'list_boards', 'list_cards', 'list_lists',
  'list_channels', 'get_channel_messages', 'list_users', 'search_messages',
  // Discord read actions
  'list_guilds', 'list_members', 'get_user'
];

export const WRITE_ACTIONS: ConnectorAction[] = [
  'send_email', 'create_draft',
  'create_doc', 'update_doc',
  'create_event', 'update_event', 'delete_event',
  'create_card', 'update_card', 'move_card', 'add_comment', 'create_list',
  'send_message', 'reply_to_thread', 'schedule_message',
  // Discord write actions
  'reply_to_message', 'send_dm'
];

export interface ConnectorAccess {
  provider: ConnectorProvider;
  actions: ConnectorAction[];
  isConnected: boolean;
  integrationId?: number;
}

export interface EmployeeConnectorMapping {
  id: number;
  employeeId: EmployeeId;
  provider: ConnectorProvider;
  actions: ConnectorAction[];
  isDefault: boolean;
}

export type PendingActionStatus = 'pending' | 'approved' | 'rejected' | 'executed' | 'failed' | 'expired' | 'cancelled';

export interface PendingConnectorAction {
  id: number;
  organizationId: number;
  userId: number;
  conversationId?: number;
  messageId?: number;
  employeeId: EmployeeId;
  provider: ConnectorProvider;
  actionType: ConnectorAction;
  actionData: Record<string, unknown>;
  previewData: Record<string, unknown>;
  status: PendingActionStatus;
  editedData?: Record<string, unknown>;
  executionResult?: Record<string, unknown>;
  errorMessage?: string;
  expiresAt?: string;
  createdAt: string;
  approvedAt?: string;
  executedAt?: string;
}

export interface ConnectorActionHistory {
  id: number;
  pendingActionId?: number;
  organizationId: number;
  userId: number;
  employeeId: EmployeeId;
  provider: ConnectorProvider;
  actionType: ConnectorAction;
  actionData: Record<string, unknown>;
  resultData?: Record<string, unknown>;
  status: 'success' | 'failed';
  errorMessage?: string;
  creditsUsed: number;
  executionTimeMs?: number;
  createdAt: string;
}

export interface ConnectorExecutionResult {
  success: boolean;
  data?: unknown;
  error?: string;
  creditsUsed?: number;
  executionTimeMs?: number;
}

// SSE Event types for connector actions
export interface ConnectorResultEvent {
  type: 'connector_result';
  provider: ConnectorProvider;
  action: ConnectorAction;
  data: unknown;
}

export interface PendingActionEvent {
  type: 'pending_action';
  id: number;
  provider: ConnectorProvider;
  action: ConnectorAction;
  preview: Record<string, unknown>;
  requiresApproval: true;
}

export interface ActionExecutedEvent {
  type: 'action_executed';
  id: number;
  provider: ConnectorProvider;
  action: ConnectorAction;
  success: boolean;
  result?: unknown;
  error?: string;
}

export type ConnectorSSEEvent = ConnectorResultEvent | PendingActionEvent | ActionExecutedEvent;

// Credit costs for connector actions
export const CONNECTOR_CREDITS = {
  READ_ACTION: 1,
  WRITE_ACTION: 2,
} as const;

// ============================================
// Cloudflare Worker Env Types
// ============================================

export interface Env {
  DB: D1Database;
  CACHE: KVNamespace;
  SESSIONS: KVNamespace;
  // FILES: R2Bucket; // Enable after R2 is activated in dashboard

  // Secrets
  JWT_SECRET: string;
  OPENAI_API_KEY: string;
  ANTHROPIC_API_KEY: string;
  STRIPE_SECRET_KEY: string;
  STRIPE_WEBHOOK_SECRET: string;
  RESEND_API_KEY: string;

  // Paddle secrets (optional - can also be stored in DB)
  PADDLE_API_KEY?: string;
  PADDLE_WEBHOOK_SECRET?: string;

  // Variables
  ENVIRONMENT: string;
  APP_URL: string;
  API_URL: string;

  // Cron secrets
  CRON_SECRET?: string;
  CREDIT_CRON_SECRET?: string;
  DAILY_SUMMARIZER_CRON_SECRET?: string;

  // Google Cloud Vertex AI (Imagen)
  VERTEX_PROJECT_ID?: string;
  VERTEX_CLIENT_EMAIL?: string;
  VERTEX_PRIVATE_KEY?: string;

  // OAuth credentials (dynamically named)
  [key: `${string}_CLIENT_ID`]: string | undefined;
  [key: `${string}_CLIENT_SECRET`]: string | undefined;
}

// ============================================
// Payment Provider Types
// ============================================

export type PaymentProvider = 'stripe' | 'paypal' | 'paddle';

export interface PaymentProviderConfig {
  provider: PaymentProvider | null;
  mode: 'test' | 'live';
  clientId: string;
  secretKey: string;
  webhookSecret?: string;
}

export interface PaymentSettings {
  id: number;
  provider: PaymentProvider;
  mode: 'test' | 'live';
  is_active: boolean;
  test_publishable_key?: string;
  test_secret_key?: string;
  live_publishable_key?: string;
  live_secret_key?: string;
  test_webhook_secret?: string;
  live_webhook_secret?: string;
  created_at: string;
  updated_at: string;
}
